// Browser side of transcripts.osmo.inc. Every call goes to the Worker, which
// decides what the caller may see. Team sessions live in HttpOnly cookies; the
// Google OAuth return lands here with tokens in the hash and they go straight
// to the Worker. Clients get a cookie for their one project from the share page.
const SUPABASE_URL = 'https://xzbjfurcheesppmlirod.supabase.co'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const write = init.method && init.method !== 'GET'
  let res: Response
  try {
    res = await fetch(`/api/${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { ...(init.body && typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}), ...(write ? { 'X-Osmo': '1' } : {}), ...(init.headers || {}) },
    })
  } catch {
    throw new ApiError(0, 'You look offline. Check your connection.')
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(res.status, String((data as { error?: string }).error || `Request failed (${res.status})`))
  return data as T
}

export const api = {
  get: <T,>(path: string) => call<T>(path),
  post: <T,>(path: string, body: unknown = {}) => call<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T,>(path: string, body: unknown = {}) => call<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T,>(path: string, body: unknown = {}) => call<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T,>(path: string) => call<T>(path, { method: 'DELETE' }),
}

export async function finishSignIn(): Promise<string | null> {
  const h = new URLSearchParams(location.hash.slice(1))
  const access = h.get('access_token')
  const refresh = h.get('refresh_token')
  const err = h.get('error_description')
  if (err) {
    history.replaceState(null, '', location.pathname + location.search)
    return err
  }
  if (!access || !refresh) return null
  history.replaceState(null, '', location.pathname + location.search)
  await api.post('session', { access_token: access, refresh_token: refresh })
  return null
}

export function signIn() {
  const back = location.origin + location.pathname + location.search
  location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(back)}&query_params=${encodeURIComponent('hd=osmo.inc&prompt=select_account')}`
}

export const signOut = () => api.post('logout')

/** Upload one file into the project's cloud folder in chunks, with progress. */
export async function uploadFile(projectId: string, file: File, onProgress: (done: number) => void, signal?: AbortSignal) {
  const start = await api.post<{ uploadId: string; dest: string; chunkSize: number }>(`projects/${projectId}/uploads`, { name: file.name, size: file.size })
  let sent = 0
  let n = 1
  while (sent < file.size) {
    const chunk = file.slice(sent, sent + start.chunkSize)
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await fetch(`/api/projects/${projectId}/uploads/${start.uploadId}/${n}?dest=${encodeURIComponent(start.dest)}&total=${file.size}`, {
          method: 'PUT', body: chunk, credentials: 'same-origin', headers: { 'X-Osmo': '1' }, signal,
        })
        if (!res.ok) throw new ApiError(res.status, ((await res.json().catch(() => ({}))) as { error?: string }).error || 'chunk failed')
        break
      } catch (e) {
        if (signal?.aborted || attempt >= 4) throw e
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
      }
    }
    sent += chunk.size
    n++
    onProgress(sent)
  }
  return api.post<{ ok: boolean }>(`projects/${projectId}/uploads/${start.uploadId}/finish`, { dest: start.dest, size: file.size })
}
